import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { parseISO } from "date-fns";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { HeaderTitle } from "../../../../components/HeaderTitle";
import { TypeUsername } from "../../../auth/enum";
import { IOptionTypeUsername } from "../../../auth/interfaces";
import { Switch } from "../../../../components/Switch";
import { Button } from "../../../../components/Button";
import { Input } from "../../../../components/Input";
import { Phone } from "../../../../components/PhoneInput";
import {
  EditPermissionsCollaborator,
  ICollaborator,
  IFormSendInviteCollaborator,
} from "../../interfaces";
import {
  Container,
  Content,
  Form,
  TitleForm,
  ContentForm,
  WrapperAccessPermissions,
  ContentSubmitButton,
  ErrorPermissions,
} from "./styles";
import { UnControlCheckbox as Checkbox } from "../../../../components/Checkbox";
import {
  editPermissions,
  loadCollaboratorById,
  resendInvivation,
} from "../../../../services/collaborators";
import { useNotification } from "../../../../hooks/useNotification";
import { TypeActionInvivation } from "../../enums";
import { useAuth } from "../../../../hooks/useAuth";
import { useTranslate } from "../../../../hooks/useTranslate";
import { formatDateToAPI } from "../../../../utils/format-date";

const Invivation = () => {
  const { successNotification, errorNotification } = useNotification();
  const navigate = useNavigate();
  const params = useParams();
  const { translate } = useTranslate();
  const { application } = useAuth();

  const [collaborator, setCollaborator] = useState<ICollaborator>(
    {} as ICollaborator
  );
  const [isLoading, setIsLoading] = useState(false);
  const [openAccessPermissions, setOpenAccessPermissions] = useState(false);
  const [signType, setSignType] = useState<TypeUsername>(TypeUsername.email);
  const [formDisabled] = useState(params?.type === TypeActionInvivation.resend);

  const schema = yup.object({
    [signType]:
      signType === TypeUsername.email
        ? yup
            .string()
            .email(translate("global.email_invalid"))
            .required(translate("global.email_required"))
        : yup.string().required(translate("global.phone_required")),
    name: yup.string().required(translate("global.name_required")),
    date_birth: yup.string().required(translate("global.dob_required")),
    office: yup.string().required(translate("global.office_required")),
    permissions: yup
      .array()
      .min(1, translate("global.permissions_min"))
      .required(translate("global.permissions_required")),
  });

  const {
    register,
    control,
    handleSubmit,
    unregister,
    reset,
    formState: { errors },
  } = useForm<IFormSendInviteCollaborator>({
    resolver: yupResolver(schema),
    defaultValues: useMemo(
      () => ({
        name: collaborator.name,
        email: collaborator.email,
        phone: collaborator.phone,
        office: collaborator.office,
        permissions: collaborator.permissions,
      }),
      [collaborator]
    ),
  });

  const selectSignType = (value: TypeUsername) => {
    unregister(value === TypeUsername.email ? "email" : "phone");
    setSignType(value);
  };

  const options: IOptionTypeUsername[] = [
    { value: TypeUsername.email, label: translate("global.email") },
    { value: TypeUsername.phone, label: translate("global.phone") },
  ];

  const handleResendInvivation = async (id: string, application: string) => {
    try {
      await resendInvivation(id, application);
      successNotification(translate("collaborator.success_message"));
    } catch {
      errorNotification(translate("collaborator.error_message"));
      setIsLoading(false);
    }
  };

  const editAccess = async ({
    application,
    permission,
    id,
  }: EditPermissionsCollaborator) => {
    try {
      await editPermissions({ id, application, permission });
      successNotification(
        translate("collaborator.update_permissions_success_message")
      );
    } catch {
      errorNotification(
        translate("collaborator.update_permissions_success_message")
      );
      setIsLoading(false);
    }
  };

  const submitForm = async ({ permissions }: IFormSendInviteCollaborator) => {
    try {
      setIsLoading(true);

      if (formDisabled) {
        await handleResendInvivation(params.id!, application);
      } else {
        await editAccess({
          id: params.id!,
          application,
          permission: permissions,
        });
      }
      setIsLoading(false);
      navigate("/collaborators");
    } catch {
      setIsLoading(false);
    }
  };

  const boot = useCallback(async (id: string) => {
    try {
      const collaborator = await loadCollaboratorById(id);

      const currentPermissions = collaborator.permissions.permission.map(
        (item: Record<string, boolean>) => Object.keys(item)[0]
      );

      setCollaborator({
        ...collaborator,
        permissions: currentPermissions,
      });
    } catch {
      errorNotification(translate("collaborator.unknow_error"));
    }
  }, []);

  useEffect(() => {
    reset({
      name: collaborator.name,
      email: collaborator.email,
      phone: collaborator.phone,
      office: collaborator.office,
      permissions: collaborator.permissions,
    });
    setSignType(collaborator?.email ? TypeUsername.email : TypeUsername.phone);
  }, [collaborator, reset]);

  useEffect(() => {
    if (params.id) boot(params.id);
  }, [params, boot]);

  return (
    <Container>
      <HeaderTitle
        title={
          formDisabled
            ? translate("collaborator.title_resend")
            : translate("collaborator.title_edit")
        }
        showIcon
        url="/collaborators"
      />
      <Content>
        <Form onSubmit={handleSubmit(submitForm)}>
          <ContentForm>
            <TitleForm>
              {formDisabled
                ? translate("collaborator.title_resend")
                : translate("collaborator.title_edit")}
            </TitleForm>
            <Switch
              currentActive={signType}
              options={options}
              disabled
              onChange={(value) => {
                selectSignType(value as any);
              }}
            />

            <Input
              {...register("name")}
              error={errors.name?.message}
              disabled={formDisabled}
              placeholder={translate(
                "collaborator.placeholder_name_collaborator"
              )}
            />

            {signType === TypeUsername.email ? (
              <Input
                {...register("email")}
                error={errors.email?.message}
                placeholder={translate("global.placeholder_email")}
                type="email"
                disabled={formDisabled}
              />
            ) : (
              <Controller
                name="phone"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Phone
                    disabled={formDisabled}
                    placeholder={translate("global.placeholder_phone")}
                    value={value}
                    onChange={onChange}
                    defaultCountry="BR"
                    error={errors.phone?.message}
                  />
                )}
              />
            )}

            {collaborator.date_birth && (
              <Input
                {...register("date_birth")}
                error={errors.date_birth?.message}
                placeholder={translate("global.placeholder_dob")}
                type="date"
                disabled={formDisabled}
                defaultValue={formatDateToAPI(
                  parseISO(collaborator.date_birth)
                )}
              />
            )}
            <Input
              {...register("office")}
              error={errors.office?.message}
              disabled={formDisabled}
              placeholder={translate("collaborator.placeholder_office")}
            />
            <Button
              label="Permissões de Acesso"
              type="button"
              onClick={() => {
                setOpenAccessPermissions(!openAccessPermissions);
              }}
            />

            {openAccessPermissions && (
              <WrapperAccessPermissions>
                <Checkbox
                  {...register("permissions")}
                  label={translate("collaborator.label_admin")}
                  labelFor="Admin"
                  value="admin"
                  defaultChecked={collaborator.permissions?.includes("admin")}
                  disabled={formDisabled}
                  extraInformation={translate("collaborator.describe_admin")}
                />
                <Checkbox
                  {...register("permissions")}
                  label={translate("collaborator.label_property")}
                  defaultChecked={collaborator.permissions?.includes(
                    "property"
                  )}
                  labelFor="property"
                  value="property"
                />
                <Checkbox
                  {...register("permissions")}
                  label={translate("collaborator.label_collaborator")}
                  defaultChecked={collaborator.permissions?.includes(
                    "collaborator"
                  )}
                  disabled={formDisabled}
                  labelFor="collaborator"
                  value="collaborator"
                />
                <Checkbox
                  {...register("permissions")}
                  label={translate("collaborator.label_animals")}
                  defaultChecked={collaborator.permissions?.includes("animals")}
                  disabled={formDisabled}
                  labelFor="animals"
                  value="animals"
                />
                <Checkbox
                  {...register("permissions")}
                  label={translate("collaborator.label_tasks")}
                  defaultChecked={collaborator.permissions?.includes("tasks")}
                  disabled={formDisabled}
                  labelFor="tasks"
                  value="tasks"
                />

                {errors.permissions != null && (
                  <ErrorPermissions>
                    {(errors.permissions as any).message}
                  </ErrorPermissions>
                )}
              </WrapperAccessPermissions>
            )}
          </ContentForm>

          <ContentSubmitButton>
            <Button
              label={
                formDisabled
                  ? translate("collaborator.title_resend")
                  : translate("collaborator.title_edit")
              }
              type="submit"
              isLoading={isLoading}
            />
          </ContentSubmitButton>
        </Form>
      </Content>
    </Container>
  );
};

export { Invivation };
